import React from "react";
import classNames from "classnames";
import { motion } from "framer-motion";
import { formatMoney } from "../../utils/formatMoney";
import { calculatePaymentPlan } from "../../utils/paymentplan";
import { Calendar, DollarSign } from "lucide-react";

const PlanBar = ({ amount, plans, change }) => {
  const currentActive = plans.find((item) => item.active);
  const currentActiveIndex = plans.findIndex((item) => item.active);

  // Calculate first month
  const getPlanDetails = () => {
    if (currentActive !== undefined) {
      const { monthlyPayment, firstMonth, numberMonths } = calculatePaymentPlan(
        currentActive
      );
      return {
        monthlyPayment,
        firstMonth,
        numberMonths,
        totalMonths: currentActive.text
      };
    }
    return null;
  };

  const planDetails = getPlanDetails();

  return (
    <div className="space-y-6">
      {/* Plan Slider */}
      <div className="relative">
        {/* Progress Track */}
        <div className="absolute top-5 left-0 right-0 h-1 bg-gray-700 rounded-full" style={{ zIndex: 1 }} />
        
        {/* Active Progress */}
        <motion.div 
          className="absolute top-5 left-0 h-1 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full"
          style={{ zIndex: 1 }}
          initial={{ width: 0 }}
          animate={{ 
            width: `${(currentActiveIndex / (plans.length - 1)) * 100}%` 
          }}
          transition={{ type: "spring", stiffness: 100, damping: 20 }}
        />

        {/* Plan Dots */}
        <div className="relative flex justify-between" style={{ zIndex: 20 }}>
          {plans.map((item, ind) => (
            <div key={`plan-${ind}`} className="relative">
              {/* Dot Button */}
              <motion.button
                onClick={() => change(ind)}
                className={classNames(
                  "relative w-10 h-10 rounded-full border-2 transition-all duration-200",
                  "flex items-center justify-center cursor-pointer",
                  "hover:scale-110 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 focus:ring-offset-gray-900",
                  ind === currentActiveIndex
                    ? "bg-gradient-to-r from-green-500 to-emerald-500 border-green-500 shadow-lg shadow-green-500/30"
                    : ind < currentActiveIndex
                    ? "bg-gray-900 border-green-500/50 ring-1 ring-green-500/20"
                    : "bg-gray-900 border-gray-600 hover:border-gray-500"
                )}
                style={{ position: 'relative', zIndex: 30 }}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
              >
                {ind === currentActiveIndex ? (
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="w-3 h-3 bg-white rounded-full"
                  />
                ) : ind < currentActiveIndex ? (
                  <div className="w-2 h-2 bg-green-400 rounded-full" />
                ) : (
                  <div className="w-2 h-2 bg-gray-500 rounded-full" />
                )}
              </motion.button>

              {/* Month Label */}
              <div className="mt-3 text-center">
                <p className={classNames(
                  "text-xs font-medium transition-colors duration-200",
                  ind === currentActiveIndex
                    ? "text-green-400"
                    : "text-gray-500"
                )}>
                  {item.text.split(' ')[0]}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Plan Details Card */}
      {planDetails && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-gradient-to-r from-green-600/10 to-emerald-600/10 rounded-xl border border-green-500/20 p-4"
        >
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
              <Calendar className="w-5 h-5 text-green-400" />
            </div>
            <div>
              <h4 className="text-white font-semibold">Payment Plan</h4>
              <p className="text-green-400 text-sm">Split into {planDetails.numberMonths} easy monthly payments</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-gray-400 text-sm">
                <DollarSign className="w-4 h-4" />
                <span>First Payment</span>
              </div>
              <p className="text-xl font-bold text-white">
                ${planDetails.firstMonth}
              </p>
            </div>

            <div className="space-y-1">
              <div className="flex items-center gap-2 text-gray-400 text-sm">
                <DollarSign className="w-4 h-4" />
                <span>Monthly After</span>
              </div>
              <p className="text-xl font-bold text-white">
                ${formatMoney(planDetails.monthlyPayment)}
              </p>
            </div>
          </div>

          <div className="mt-3 pt-3 border-t border-green-500/20">
            <p className="text-sm text-gray-300">
              One payment of <span className="font-semibold text-green-400">${planDetails.firstMonth}</span> then{" "}
              <span className="font-semibold text-green-400">${formatMoney(planDetails.monthlyPayment)}/month</span>{" "}
              for {planDetails.numberMonths - 1} months
            </p>
          </div>
        </motion.div>
      )}

      {/* No Plan Selected */}
      {!planDetails && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="bg-gray-800/30 rounded-xl border border-gray-700 p-4 text-center"
        >
          <p className="text-gray-400">Please select a plan duration</p>
        </motion.div>
      )}
    </div>
  );
};

export default PlanBar;